import React, { createContext, PropsWithChildren, useCallback, useContext, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useDeepCompareEffect, useDeepMemo } from '../../../../hooks';
import { DATA_TABLE_LIMIT } from '../getDataTableCount';
import { useDataTableContext } from '../DataTableContext';
import { useObservable, useObserver } from '../../../../utils/useObservable';
import {
  InternalQueryModel,
  OnVariablesChanged,
  QueryModel,
  QuerySettings,
  SearchModel,
  SortModel,
  SortModelQuery
} from './DataTableQuery.types';
import { useDataTableSearchParams } from './useDataTableSearchParams';
import { useDataTableInitialSearchModelObserver } from './DataTableInitialSearchModelProvider';
import { useSortChanges } from './queryVariableChangeSources/useSortChanges';
import { useQueryChanges } from './queryVariableChangeSources/useQueryChanges';

export interface QueryContext<Q extends QueryModel = QueryModel, S extends SortModel = SortModel> {
  queryVariables: OnVariablesChanged<Q, S> | null;
  applyQueryVariables: (nextModel: Partial<InternalQueryModel<Q, S>>, persist?: boolean) => void;
  applyQuery: (nextQuery: Partial<Q>, options: { mode: 'patch' | 'replace'; persist: boolean }) => void;
  applyPage: (nextPage: number) => void;
  applySort: (nextSort: SortModelQuery<S>[]) => void;
}

const fn = () => {};

export const DataTableQueryContext = createContext<QueryContext<any, any>>({
  queryVariables: null,
  applyQueryVariables: fn,
  applyQuery: fn,
  applyPage: fn,
  applySort: fn
});

export const DataTableQueryProvider = ({
  querySettings,
  dataTableId,
  children
}: PropsWithChildren<DataTableQueryProps>) => {
  const { onVariablesChanged, setPageIndex: gotoPage, setSorting: setSortBy } = useDataTableContext();
  const observableQueryVariables = useObservable<OnVariablesChanged<any, any> | null>(
    `dataTable:queryProvider:${dataTableId}`
  );
  const observableFilterChange = useObservable<boolean>(`dataTable:queryProvider:filter:${dataTableId}`);
  const observableSortChange = useObservable<boolean>(`dataTable:queryProvider:page:${dataTableId}`);
  const observablePageChange = useObservable<boolean>(`dataTable:queryProvider:sort:${dataTableId}`);
  const { saveParams } = useDataTableSearchParams<QueryModel, SortModel>();
  const limit = querySettings?.limit || DATA_TABLE_LIMIT;

  const [internalQueryModel, setInternalQueryModel] = useState<Partial<
    InternalQueryModel<QueryModel, SortModel>
  > | null>(null);

  const persistState = useCallback(
    (nextModel: Partial<InternalQueryModel<QueryModel, SortModel>>) => {
      const { page, ...nextSearchModel } = nextModel;
      saveParams({ ...nextSearchModel });
    },
    [saveParams]
  );

  useDeepCompareEffect(
    () => {
      if (internalQueryModel) {
        const { query, sort, page, persist } = clone(internalQueryModel);
        const nextVariables = { query, sort: sort ?? [], page };
        onVariablesChanged?.(clone({ query, sort: sort ?? [], page }));
        observableQueryVariables.notify(clone(nextVariables));

        if (persist) {
          const { persist, ...modelToPersist } = clone(internalQueryModel);
          persistState(modelToPersist);
        }
      }
    },
    [internalQueryModel],
    [observableQueryVariables, onVariablesChanged, persistState]
  );

  const applyQueryVariables = useCallback(
    (partialNextModel: Partial<InternalQueryModel<QueryModel, SortModel>>, persist = false) => {
      setInternalQueryModel((prevModel) => ({ ...prevModel, ...partialNextModel, persist }));
      if (!isEqual(internalQueryModel?.sort, partialNextModel?.sort)) {
        const targetSort = partialNextModel?.sort?.[0];
        setSortBy?.(targetSort ? [{ id: targetSort.type, desc: targetSort.reversed }] : []);
      }
    },
    [setSortBy, internalQueryModel?.sort]
  );

  const applyQuery = useCallback(
    (nextQuery: Partial<QueryModel>, options: { mode: 'patch' | 'replace'; persist: boolean }) => {
      setInternalQueryModel((prevModel) =>
        options.mode === 'replace'
          ? ({
              ...prevModel,
              query: { search: prevModel?.query?.search || undefined, ...nextQuery },
              page: { offset: 0, limit },
              persist: options.persist
            } as Partial<InternalQueryModel<QueryModel, string>>)
          : ({
              ...prevModel,
              query: { ...prevModel?.query, ...nextQuery },
              page: { offset: 0, limit },
              persist: options.persist
            } as Partial<InternalQueryModel<QueryModel, string>>)
      );
      observableFilterChange.notify(true);
      gotoPage?.(0);
    },
    [gotoPage, limit, observableFilterChange]
  );

  const applyPage = useCallback(
    (nextPage: number) => {
      setInternalQueryModel((prevModel) => ({ ...prevModel, page: { limit, offset: nextPage * limit } }));
      gotoPage?.(nextPage);
      observablePageChange.notify(true);
    },
    [gotoPage, limit, observablePageChange]
  );

  const applySort = useCallback(
    (nextSort: SortModelQuery<SortModel>[] | undefined, persist = false) => {
      setInternalQueryModel((prevModel) => ({
        ...prevModel,
        sort: nextSort,
        page: { offset: 0, limit },
        persist
      }));
      gotoPage?.(0);
      observableSortChange.notify(true);
    },
    [gotoPage, limit, observableSortChange]
  );

  const applyInitialQueryVariables = useCallback(
    ({ detail: initialSearchModel }: CustomEvent<SearchModel<QueryModel, SortModel>>) => {
      const initialQueryVariables = {
        query: initialSearchModel.query,
        sort: initialSearchModel.sort,
        page: { limit, offset: 0 },
        quickReportId: initialSearchModel?.quickReportId
      };
      applyQueryVariables(initialQueryVariables, true);
    },
    [applyQueryVariables, limit]
  );

  useDataTableInitialSearchModelObserver(dataTableId, applyInitialQueryVariables);
  useSortChanges(applySort, internalQueryModel !== null);
  useQueryChanges(applyQuery, internalQueryModel?.query, internalQueryModel !== null);

  const context: QueryContext = useDeepMemo(
    () => ({
      applyQueryVariables,
      applyQuery,
      applyPage,
      applySort,
      observableQueryVariables,
      observableSortChange,
      observablePageChange,
      observableFilterChange,
      queryVariables: internalQueryModel
        ? {
            query: internalQueryModel.query,
            sort: internalQueryModel.sort,
            page: internalQueryModel.page
          }
        : null
    }),
    [
      applyQueryVariables,
      applyPage,
      applyQuery,
      applySort,
      internalQueryModel,
      observableQueryVariables,
      observableSortChange,
      observablePageChange,
      observableFilterChange
    ]
  );
  return <DataTableQueryContext.Provider value={context}>{children}</DataTableQueryContext.Provider>;
};

export const useDataTableQueryContext = <Q extends QueryModel, S extends SortModel>(): QueryContext<Q, S> =>
  useContext<QueryContext<Q, S>>(DataTableQueryContext);

interface DataTableQueryProps {
  querySettings?: QuerySettings;
  dataTableId: string;
}

export function useDataTableQueryObserver(
  dataTableId: string,
  fn: (data: CustomEvent<OnVariablesChanged<any, any> | null>) => void
) {
  useObserver(`dataTable:queryProvider:${dataTableId}`, fn);
}

export function useDataTableFilterChangeObserver(dataTableId: string, fn: (data: CustomEvent<boolean | null>) => void) {
  useObserver(`dataTable:queryProvider:filter:${dataTableId}`, fn);
}

export function useDataTableSortChangeObserver(dataTableId: string, fn: (data: CustomEvent<boolean | null>) => void) {
  useObserver(`dataTable:queryProvider:sort:${dataTableId}`, fn);
}

export function useDataTablePageChangeObserver(dataTableId: string, fn: (data: CustomEvent<boolean | null>) => void) {
  useObserver(`dataTable:queryProvider:sort:${dataTableId}`, fn);
}

function clone<T>(data: T): T {
  return 'structuredClone' in window ? structuredClone(data) : { ...data };
}
